import {
  CustomFieldEntityEnum,
  ICustomFieldResSchema,
} from '@monetize/types/app';
import { useMemo } from 'react';
import { useFlags } from '../services/launchDarkly';
import { sortCustomFields } from '../utils/customFields';
import { useCustomFields } from './useCustomFields';

/**
 * The USAGE_EVENT custom fields the tenant has configured.
 *
 * Usage event custom fields are sent to the usage service as `properties`,
 * which ignores role permissions and drops any key that is not configured, so
 * hidden fields are included: tables, the CSV template, and upload validation
 * all need the full configured key set.
 *
 * Fetch is skipped when the usage custom field UI is flagged off.
 */
export const useUsageEventCustomFields = () => {
  const { enableUsageCustomFields } = useFlags();
  const { customFieldList, isLoading, isError } = useCustomFields(
    CustomFieldEntityEnum.USAGE_EVENT,
    true,
    !!enableUsageCustomFields,
  );

  const customFields = useMemo(
    () => [...customFieldList].sort(sortCustomFields),
    [customFieldList],
  );

  const customFieldsByKey = useMemo(
    () =>
      customFields.reduce<Record<string, ICustomFieldResSchema>>(
        (acc, field) => {
          acc[field.key] = field;
          return acc;
        },
        {},
      ),
    [customFields],
  );

  const configuredKeys = useMemo(
    () => customFields.map(({ key }) => key),
    [customFields],
  );

  return {
    customFields,
    customFieldsByKey,
    configuredKeys,
    hasCustomFields: customFields.length > 0,
    isLoading: enableUsageCustomFields ? isLoading : false,
    /**
     * A failed fetch is indistinguishable from "no fields configured" once the
     * query settles, so callers that write properties must block on this.
     */
    isError: enableUsageCustomFields ? isError : false,
  };
};
